import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { recapService } from '../services';
import { FaTrophy, FaStar, FaChevronRight, FaChevronLeft } from 'react-icons/fa';
import './CharacterRecap.css';

const CharacterRecap = () => {
  const { characterId } = useParams();
  const navigate = useNavigate();
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [year, setYear] = useState(2025);

  useEffect(() => {
    loadRecap();
  }, [characterId, year]);

  const loadRecap = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await recapService.getCharacterRecap(characterId, year);
      setRecap(response.data);
    } catch (err) {
      console.error('Error loading recap:', err);
      if (err.response?.status === 403) {
        setError('Você não possui autorização para visualizar este personagem');
      } else if (err.response?.status === 404) {
        setError('Personagem não encontrado');
      } else {
        setError('Erro ao carregar retrospectiva. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const nextSection = () => {
    if (currentSection < 6) {
      setCurrentSection(currentSection + 1);
    }
  };

  const prevSection = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  if (loading) {
    return (
      <div className="recap-loading">
        <div className="loader"></div>
        <p>Carregando sua retrospectiva épica...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="recap-error">
        <h2>⚠️ {error}</h2>
        <button onClick={() => navigate('/app/recap')} className="btn-back">
          Voltar
        </button>
      </div>
    );
  }

  if (!recap) return null;

  const { character, stats, archetype, items, relics, events, pointsEvolution, topUpgrades, classRanking, participation } = recap;

  return (
    <div className="recap-container">
      {/* Section 0: Opening */}
      {currentSection === 0 && (
        <div className="recap-section recap-opening fade-in">
          <div className="recap-year">{year}</div>
          <h1 className="recap-title epic-text">{character.name}</h1>
          <div className="recap-class">{character.class}</div>
          <p className="recap-subtitle">Sua Jornada em {year}</p>
          <button onClick={nextSection} className="btn-next">
            Iniciar Jornada <FaChevronRight />
          </button>
        </div>
      )}

      {/* Section 1: Stats Overview */}
      {currentSection === 1 && (
        <div className="recap-section recap-stats fade-in">
          <h2 className="section-title">Sua Jornada em {year}</h2>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">⚔️</div>
              <div className="stat-value">{character.level}</div>
              <div className="stat-label">Level Atual</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📈</div>
              <div className="stat-value">+{stats.levelGain}</div>
              <div className="stat-label">Levels Ganhos</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⭐</div>
              <div className="stat-value">{stats.totalPoints.toLocaleString()}</div>
              <div className="stat-label">Pontos Conquistados</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🎯</div>
              <div className="stat-value">{stats.eventsCompleted}</div>
              <div className="stat-label">Eventos Concluídos</div>
            </div>
          </div>
          
          {/* Points Evolution Chart */}
          {pointsEvolution.length > 0 && (
            <div className="points-evolution">
              <h3>Evolução de Pontos</h3>
              <div className="chart">
                {pointsEvolution.map((month) => (
                  <div key={month.month} className="chart-bar">
                    <div 
                      className="bar" 
                      style={{ 
                        height: `${(month.points / Math.max(...pointsEvolution.map(m => m.points))) * 100}%` 
                      }}
                    >
                      <span className="bar-value">{month.points}</span>
                    </div>
                    <span className="bar-label">{month.monthName}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="section-nav">
            <button onClick={prevSection} className="btn-prev">
              <FaChevronLeft /> Anterior
            </button>
            <button onClick={nextSection} className="btn-next">
              Próximo <FaChevronRight />
            </button>
          </div>
        </div>
      )}

      {/* Section 2: Class Ranking & Participation */}
      {currentSection === 2 && (
        <div className="recap-section recap-ranking fade-in">
          <h2 className="section-title">Sua Posição no Revolution</h2>
          
          {/* Class Ranking */}
          <div className="ranking-card">
            <div className="ranking-icon">🏆</div>
            <div className="ranking-content">
              <h3>Ranking de Classe</h3>
              <div className="ranking-position">
                <span className="position-number">{classRanking.position}º</span>
                <span className="position-total">de {classRanking.total}</span>
              </div>
              <p className="ranking-message">{classRanking.message}</p>
            </div>
          </div>

          {/* Event Participation */}
          <div className="participation-card">
            <div className="participation-icon">📅</div>
            <div className="participation-content">
              <h3>Participação em Eventos</h3>
              <div className="participation-stats">
                <div className="participation-bar">
                  <div 
                    className="participation-fill" 
                    style={{ width: `${participation.percentage}%` }}
                  >
                    <span className="participation-percentage">{participation.percentage}%</span>
                  </div>
                </div>
                <div className="participation-numbers">
                  {participation.participated} de {participation.total} eventos
                </div>
              </div>
              <p className="participation-message">{participation.message}</p>
            </div>
          </div>

          <div className="section-nav">
            <button onClick={prevSection} className="btn-prev">
              <FaChevronLeft /> Anterior
            </button>
            <button onClick={nextSection} className="btn-next">
              Próximo <FaChevronRight />
            </button>
          </div>
        </div>
      )}

      {/* Section 3: Equipment Highlights */}
      {currentSection === 3 && (
        <div className="recap-section recap-equipment fade-in">
          <h2 className="section-title">Destaques do Ano</h2>
          
          {topUpgrades.length > 0 && (
            <div className="highlight-section">
              <h3>🛡️ Principais Upgrades</h3>
              <div className="upgrades-list">
                {topUpgrades.map((upgrade, index) => (
                  <div key={index} className="upgrade-item">
                    <span className="upgrade-rank">#{index + 1}</span>
                    <span className="upgrade-name">{upgrade.name}</span>
                    <span className="upgrade-level">Level {upgrade.level}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {relics.length > 0 && (
            <div className="highlight-section">
              <h3>💎 Relíquias Obtidas ({relics.length})</h3>
              <div className="relics-list">
                {relics.map((relic, index) => (
                  <div key={index} className="relic-item">
                    <FaStar className="relic-icon" />
                    <span className="relic-name">{relic.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="section-nav">
            <button onClick={prevSection} className="btn-prev">
              <FaChevronLeft /> Anterior
            </button>
            <button onClick={nextSection} className="btn-next">
              Próximo <FaChevronRight />
            </button>
          </div>
        </div>
      )}

      {/* Section 4: Challenges */}
      {currentSection === 4 && (
        <div className="recap-section recap-challenges fade-in">
          <h2 className="section-title">Desafios Superados</h2>
          
          <div className="challenges-grid">
            <div className="challenge-card">
              <div className="challenge-icon">⚔️</div>
              <div className="challenge-value">{events.pvp}</div>
              <div className="challenge-label">Batalhas PVP</div>
            </div>
            <div className="challenge-card">
              <div className="challenge-icon">🏰</div>
              <div className="challenge-value">{events.blessCastle}</div>
              <div className="challenge-label">Bless Castle</div>
            </div>
            <div className="challenge-card">
              <div className="challenge-icon">🎯</div>
              <div className="challenge-value">{events.quests}</div>
              <div className="challenge-label">Quests Concluídas</div>
            </div>
          </div>

          <div className="total-events">
            <FaTrophy className="trophy-icon" />
            <div>
              <div className="total-number">{events.completed}</div>
              <div className="total-label">Total de Eventos Concluídos em {year}</div>
            </div>
          </div>

          <div className="section-nav">
            <button onClick={prevSection} className="btn-prev">
              <FaChevronLeft /> Anterior
            </button>
            <button onClick={nextSection} className="btn-next">
              Próximo <FaChevronRight />
            </button>
          </div>
        </div>
      )}

      {/* Section 5: Archetype */}
      {currentSection === 5 && (
        <div className="recap-section recap-archetype fade-in">
          <h2 className="section-title">Seu Perfil no Clã Revolution</h2>
          
          <div className="archetype-card">
            <div className="archetype-badge">
              <FaTrophy className="badge-icon" />
            </div>
            <h3 className="archetype-title">{archetype.title}</h3>
            <p className="archetype-description">{archetype.description}</p>
          </div>

          <div className="archetype-signature">
            <div className="signature-line"></div>
            <p className="signature-text">Clã Revolution</p>
          </div>

          <div className="section-nav">
            <button onClick={prevSection} className="btn-prev">
              <FaChevronLeft /> Anterior
            </button>
            <button onClick={nextSection} className="btn-next">
              Próximo <FaChevronRight />
            </button>
          </div>
        </div>
      )}

      {/* Section 6: Closing */}
      {currentSection === 6 && (
        <div className="recap-section recap-closing fade-in">
          <div className="closing-content">
            <h2 className="closing-title">Parabéns, {character.name}!</h2>
            
            <div className="closing-message">
              <p>
                Feliz Natal e um Ano Novo repleto de conquistas épicas! 
                Que em {year + 1} possamos contar novamente com sua força, 
                dedicação e espírito guerreiro.
              </p>
              <p>
                O clã Revolution segue mais forte e unido com você ao nosso lado. 
                Sua presença faz toda a diferença em cada batalha, cada desafio, 
                cada vitória.
              </p>
            </div>

            <div className="closing-battle-cry">
              <h3>AVANTE REVO!</h3>
            </div>

            <div className="closing-year" style={{ position: 'relative', display: 'inline-block' }}>
              <span style={{ 
                textDecoration: 'line-through', 
                opacity: 0.5,
                display: 'block'
              }}>
                {year}
              </span>
              <span style={{ 
                position: 'absolute',
                top: '-30px',
                left: '50%',
                transform: 'translateX(-50%)',
                fontSize: '4rem',
                fontWeight: 900,
                background: 'linear-gradient(135deg, #38bdf8 0%, #0ea5e9 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                animation: 'glow 2s ease-in-out infinite'
              }}>
                {year + 1}
              </span>
            </div>

            <div className="closing-buttons">
              <button onClick={prevSection} className="btn-prev">
                <FaChevronLeft /> Rever
              </button>
              <button onClick={() => navigate('/app/dashboard')} className="btn-finish">
                Finalizar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Progress indicator */}
      <div className="progress-dots">
        {[0, 1, 2, 3, 4, 5].map((index) => (
          <span
            key={index}
            className={`dot ${currentSection === index ? 'active' : ''}`}
            onClick={() => setCurrentSection(index)}
          />
        ))}
      </div>
    </div>
  );
};

export default CharacterRecap;
